import { isEqual } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, type Ref, ref } from "vue";

import { ErrorDialog, ToastDialog } from "shared/boot/alert";
import { track } from "shared/boot/analytics";
import { getLocalePluralText, getLocaleText } from "shared/boot/i18n";
import DialogService from "shared/modals/DialogService";
import { DialogTheme } from "shared/modals/types";
import { ExternalItem, StreamsExternalItem } from "shared/resources";
import { saveExternalItems } from "shared/services/externalItem";
import useStreamsStore from "shared/stores/streams";
import useUserStore from "shared/stores/user";
import { type CurrentUser } from "shared/stores/user/types";
import { type Maybe } from "shared/types";

const TOAST_DIALOG_COLOR = "grey-7";

export default function useExternalItemForm(props: {
  externalItemId?: number;
  prefilledUrl?: string;
  streamId: number;
}) {
  const { getStreamById } = useStreamsStore();

  const { currentUser }: { currentUser: Ref<CurrentUser> } =
    storeToRefs(useUserStore());

  const externalItem: Ref<Maybe<ExternalItem>> = ref();

  const initialExternalItemChanges: Ref<ReturnType<ExternalItem["changes"]>> =
    ref({});

  const selectedStreamIds: Ref<number[]> = ref([]);
  const loading: Ref<boolean> = ref(true);
  const acceptConditions: Ref<boolean> = ref(false);

  const action = computed<string>(() =>
    props.externalItemId ? "edit" : "create"
  );

  const selectedStreamLength = computed<number>(
    () => externalItem.value?.streamsExternalItems?.length || 0
  );

  const firstStreamName = computed<Maybe<string>>(() => {
    if (!externalItem.value) return "";

    const streamId = externalItem.value.streamsExternalItems[0]?.streamId;
    const stream = getStreamById(streamId!);

    return stream?.label;
  });

  async function loadExternalItem() {
    if (!props.externalItemId) {
      externalItem.value = new ExternalItem({
        advertisementRateCurrency: currentUser.value.currency_code,
        audience: null,
        publishedAt: new Date(),
        sourceName: null,
        streamsExternalItems: [],
        summary: "",
        timeZone: currentUser.value.time_zone,
        title: "",
        url: props.prefilledUrl || "",
      });

      initialExternalItemChanges.value = externalItem.value.changes();

      selectedStreamIds.value = [Number(props.streamId)];

      loading.value = false;

      return;
    }

    try {
      externalItem.value = (
        await ExternalItem.includes(["streamsExternalItems"]).find(
          props.externalItemId
        )
      ).data;

      selectedStreamIds.value = externalItem.value.streamsExternalItems.map(
        (streamsExternalItem) => streamsExternalItem.streamId
      );
    } catch (error) {
      ErrorDialog(
        getLocaleText("external_item_edit.load_external_item_error"),
        error
      );
    } finally {
      loading.value = false;
    }
  }

  async function saveExternalItem(onContinue?: () => void) {
    track("Clicked Save in External Item Form");

    const canSave = await prepareToSave();
    if (!canSave) return;

    try {
      await saveExternalItems([externalItem.value!]);
    } catch (error) {
      handleSavingError(error);

      return;
    }

    track("Created an External Item");

    displaySavingSuccessMessage();

    onContinue?.();
  }

  function displaySavingSuccessMessage() {
    let actionMessage;

    if (action.value === "edit") {
      actionMessage = getLocaleText(
        "external_item_edit.external_item_updated",
        { externalItem: externalItem.value!.title }
      );
    } else {
      actionMessage = getLocalePluralText(
        "external_item_edit.external_item_added",
        selectedStreamLength.value - 1,
        {
          externalItem: externalItem.value!.title,
          firstStreamName: firstStreamName.value,
          length: selectedStreamLength.value - 1,
        }
      );
    }

    showNotificationToast(actionMessage);
  }

  function handleSavingError(error: unknown) {
    ErrorDialog(
      getLocaleText("external_item_edit.save_external_item_error"),
      error
    );
  }

  function prepareToSave() {
    if (!acceptConditions.value) return false;

    prepareStreamsExternalItems();

    return true;
  }

  function prepareStreamsExternalItems() {
    const existingStreamIds = externalItem.value!.streamsExternalItems.map(
      ({ streamId }) => streamId
    );

    // delete existing records that are no longer selected
    externalItem.value!.streamsExternalItems.forEach((streamsExternalItem) => {
      if (!selectedStreamIds.value.includes(streamsExternalItem.streamId)) {
        Object.assign(streamsExternalItem, { isMarkedForDestruction: true });
      }
    });

    // add new rows
    selectedStreamIds.value
      .filter((id) => !existingStreamIds.includes(id))
      .forEach((streamId) => {
        const streamsExternalItem = new StreamsExternalItem({
          streamId,
        });

        externalItem.value!.streamsExternalItems.push(streamsExternalItem);
      });
  }

  function confirmDeleteExternalItem(onContinue?: () => void) {
    track("Clicked Delete ExternalItem from External Item Form");

    DialogService.confirm({
      dialogMaxWidth: "320px",
      message: getLocaleText("external_item_edit.delete_external_item_message"),
      persistent: true,
      theme: DialogTheme.DANGER,
      title: getLocaleText("external_item_edit.delete_external_item_title"),
    }).onOk(async () => {
      await deleteExternalItem();
      onContinue?.();
    });
  }

  async function deleteExternalItem() {
    try {
      await externalItem.value!.destroy();

      track("Deleted External Item");

      showNotificationToast(
        getLocaleText("external_item_edit.external_item_deleted")
      );
    } catch (error) {
      ErrorDialog(
        getLocaleText("external_item_edit.delete_external_item_error"),
        error
      );
    }
  }

  function confirmCancelEditExternalItem(onContinue: () => void) {
    const isEqualChanges = isEqual(
      initialExternalItemChanges.value,
      externalItem.value!.changes()
    );

    if (isEqualChanges) {
      onContinue?.();

      return;
    }

    DialogService.confirm({
      message: getLocaleText("external_item_edit.changes_not_saved"),
      persistent: true,
      theme: DialogTheme.DANGER,
      title: getLocaleText("external_item_edit.leave_this_page"),
    }).onOk(onContinue);
  }

  function showNotificationToast(message: string) {
    ToastDialog(message, { color: TOAST_DIALOG_COLOR });
  }

  return {
    acceptConditions,
    action,
    confirmCancelEditExternalItem,
    confirmDeleteExternalItem,

    externalItem,
    loadExternalItem,
    loading,
    prepareToSave,
    saveExternalItem,
    selectedStreamIds,
  };
}
